import React, { useState, useRef } from "react";
import "./PassengerForm.css";
import MeetingDatePicker from "./MeetingDatePicker";
import { createRequest } from "../services/firebaseApi";
import { getNextMeetingsAsync } from "../utils/dateHelpers";
import NameAutocomplete from "./NameAutocomplete";
import AddressAutocomplete from "./AddressAutocomplete";
import { upsertProfile, getByNameExact } from "../services/profileStore";
import { addMyRequest } from "../services/myRequests";

const PassengerForm = ({
  onSubmit,
  onBack,
  showNotification,
  onGoToWaitingList,
}) => {
  const [formData, setFormData] = useState({
    passengerName: "",
    address: "",
    city: "",
    postalCode: "",
    returnDifferent: false,
    returnAddress: "",
    returnCity: "",
    returnPostalCode: "",
    meetingAt: null,
    isoDate: "",
    meetingLabel: "",
    routine: false,
    selectedMeetings: [], // tableau des meetingAt sélectionnés
    seatsNeeded: 1, // nombre de places demandées
    icon: undefined, // emoji facultatif
    description: "", // description optionnelle
    realAddress: undefined, // adresse réelle pour le géocodage (si adresse de covoiturage)
    latitude: undefined, // coordonnées GPS si disponibles
    longitude: undefined,
    returnRealAddress: undefined,
    returnLatitude: undefined,
    returnLongitude: undefined,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMeetingChange = (meeting) => {
    setFormData((prev) => {
      // si multi, toggle dans selectedMeetings
      if (!prev.routine && Array.isArray(prev.selectedMeetings)) {
        const exists = prev.selectedMeetings.includes(meeting.meetingAt);
        const nextSelected = exists
          ? prev.selectedMeetings.filter((x) => x !== meeting.meetingAt)
          : [...prev.selectedMeetings, meeting.meetingAt];
        return {
          ...prev,
          selectedMeetings: nextSelected,
          // garder un dernier choix pour compat simple
          meetingAt: meeting.meetingAt,
          isoDate: meeting.isoDate,
          meetingLabel: meeting.label,
        };
      }
      // mode simple
      return {
        ...prev,
        meetingAt: meeting.meetingAt,
        isoDate: meeting.isoDate,
        meetingLabel: meeting.label,
      };
    });
  };

  const handleSelectProfile = async (p) => {
    // Pré-remplir les champs de base
    setFormData((prev) => ({
      ...prev,
      passengerName: p.name || prev.passengerName,
      icon: p.icon || prev.icon,
    }));

    // Si on a une ville et un code postal mais pas d'adresse complète, essayer de compléter
    if (p.city && p.postalCode && (!p.address || p.address.trim() === "")) {
      // Essayer de trouver une adresse complète via l'API
      try {
        const { searchAddresses } = await import("../services/addressAutocomplete");
        const query = `${p.city} ${p.postalCode}`;
        const suggestions = await searchAddresses(query, 1);
        
        if (suggestions && suggestions.length > 0) {
          const bestMatch = suggestions[0];
          setFormData((prev) => ({
            ...prev,
            address: bestMatch.street || bestMatch.fullAddress || prev.address,
            city: bestMatch.city || p.city || prev.city,
            postalCode: bestMatch.postcode || p.postalCode || prev.postalCode,
          }));
        } else {
          // Si pas de suggestion, utiliser au moins ce qu'on a
          setFormData((prev) => ({
            ...prev,
            city: p.city || prev.city,
            postalCode: p.postalCode || prev.postalCode,
          }));
        }
      } catch (error) {
        console.warn("Erreur lors de la complétion d'adresse:", error);
        // En cas d'erreur, utiliser au moins ce qu'on a
        setFormData((prev) => ({
          ...prev,
          address: p.address || prev.address,
          city: p.city || prev.city,
          postalCode: p.postalCode || prev.postalCode,
        }));
      }
    } else {
      // Si on a déjà une adresse complète, l'utiliser directement
      setFormData((prev) => ({
        ...prev,
        address: p.address != null && p.address !== "" ? p.address : prev.address,
        city: p.city != null && p.city !== "" ? p.city : prev.city,
        postalCode:
          p.postalCode != null && p.postalCode !== ""
            ? p.postalCode
            : prev.postalCode,
      }));
    }
  };

  const handleAddressChange = (addressData) => {
    console.log("📥 PassengerForm reçoit:", addressData);

    const newData = {
      address: addressData.address || "",
      city: addressData.city || "",
      postalCode: addressData.postcode || "",
      realAddress: addressData.realAddress, // Adresse réelle pour le géocodage
      latitude: addressData.latitude,
      longitude: addressData.longitude,
    };

    console.log("📝 Mise à jour avec:", newData);

    setFormData((prev) => {
      const updated = {
        ...prev,
        ...newData,
      };
      
      console.log("🔄 formData mis à jour:", updated);
      return updated;
    });
  };

  const handleReturnAddressChange = (addressData) => {
    console.log("📥 PassengerForm reçoit (retour):", addressData);

    const newData = {
      returnAddress: addressData.address || "",
      returnCity: addressData.city || "",
      returnPostalCode: addressData.postcode || "",
      returnRealAddress: addressData.realAddress,
      returnLatitude: addressData.latitude,
      returnLongitude: addressData.longitude,
    };

    setFormData((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const handleLocationFound = (locationData) => {
    console.log("📍 handleLocationFound appelé avec:", locationData);
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        address: locationData.address || "",
        city: locationData.city || "",
        postalCode: locationData.postalCode || "",
      };
      console.log(
        "📝 handleLocationFound: formData mis à jour:",
        updatedFormData,
      );
      return updatedFormData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    if (
      !formData.passengerName ||
      !formData.address ||
      !formData.city ||
      !formData.postalCode
    ) {
      showNotification &&
        showNotification(
          "Veuillez remplir tous les champs (prénom, adresse, ville, code postal).",
          "error",
        );
      setSubmitting(false);
      return;
    }

    if (formData.returnDifferent) {
      if (!formData.returnAddress || !formData.returnCity || !formData.returnPostalCode) {
        showNotification &&
          showNotification(
            "Veuillez renseigner l'adresse de retour (adresse, ville, code postal).",
            "error",
          );
        setSubmitting(false);
        return;
      }
    }
    if (
      !formData.routine &&
      !formData.meetingAt &&
      (!formData.selectedMeetings || formData.selectedMeetings.length === 0)
    ) {
      showNotification &&
        showNotification(
          "Veuillez choisir au moins une date de réunion.",
          "error",
        );
      setSubmitting(false);
      return;
    }

    try {
      let created = 0;
      if (formData.routine) {
        const meetings = await getNextMeetingsAsync(8);
        const results = await Promise.all(
          meetings.map((m) =>
                          createRequest({
                name: formData.passengerName,
                address: formData.address,
                city: formData.city,
                postalCode: formData.postalCode,
                returnDifferent: formData.returnDifferent,
                returnAddress: formData.returnDifferent ? formData.returnAddress : undefined,
                returnCity: formData.returnDifferent ? formData.returnCity : undefined,
                returnPostalCode: formData.returnDifferent ? formData.returnPostalCode : undefined,
                meetingAt: m.meetingAt,
                isoDate: m.isoDate,
                seatsNeeded: formData.seatsNeeded,
                icon: formData.icon,
                description: formData.description || undefined,
                realAddress: formData.realAddress, // Adresse réelle pour le géocodage
                latitude: formData.latitude,
                longitude: formData.longitude,
                returnRealAddress: formData.returnDifferent ? formData.returnRealAddress : undefined,
                returnLatitude: formData.returnDifferent ? formData.returnLatitude : undefined,
                returnLongitude: formData.returnDifferent ? formData.returnLongitude : undefined,
              }),
          ),
        );

        // Sauvegarder les IDs des demandes créées
        results.forEach((result) => {
          if (result && result.key) {
            addMyRequest(result.key);
          }
        });

        created = meetings.length;
      } else {
        // si plusieurs dates sélectionnées, créer une demande par date
        const allMeetings = await getNextMeetingsAsync(8);
        const meetingsList =
          formData.selectedMeetings && formData.selectedMeetings.length > 0
            ? allMeetings.filter((m) =>
                formData.selectedMeetings.includes(m.meetingAt),
              )
            : [{ meetingAt: formData.meetingAt, isoDate: formData.isoDate }];

        const results = await Promise.all(
          meetingsList.map((m) =>
                          createRequest({
                name: formData.passengerName,
                address: formData.address,
                city: formData.city,
                postalCode: formData.postalCode,
                returnDifferent: formData.returnDifferent,
                returnAddress: formData.returnDifferent ? formData.returnAddress : undefined,
                returnCity: formData.returnDifferent ? formData.returnCity : undefined,
                returnPostalCode: formData.returnDifferent ? formData.returnPostalCode : undefined,
                meetingAt: m.meetingAt,
                isoDate: m.isoDate,
                seatsNeeded: formData.seatsNeeded,
                icon: formData.icon,
                description: formData.description || undefined,
                realAddress: formData.realAddress, // Adresse réelle pour le géocodage
                latitude: formData.latitude,
                longitude: formData.longitude,
                returnRealAddress: formData.returnDifferent ? formData.returnRealAddress : undefined,
                returnLatitude: formData.returnDifferent ? formData.returnLatitude : undefined,
                returnLongitude: formData.returnDifferent ? formData.returnLongitude : undefined,
              }),
          ),
        );

        // Sauvegarder les IDs des demandes créées
        results.forEach((result) => {
          if (result && result.key) {
            addMyRequest(result.key);
          }
        });

        created = meetingsList.length;
      }

      // Vérifier si l'adresse a changé par rapport au profil enregistré
      // et demander confirmation pour mettre à jour le profil
      try {
        const existingProfile = getByNameExact(formData.passengerName);
        if (existingProfile && existingProfile.address) {
          const oldAddress = `${existingProfile.address || ""}, ${existingProfile.postalCode || ""} ${existingProfile.city || ""}`.trim();
          const newAddress = `${formData.address}, ${formData.postalCode} ${formData.city}`.trim();
          
          // Si l'adresse est différente, demander confirmation
          if (oldAddress && newAddress && oldAddress !== newAddress) {
            const shouldUpdate = window.confirm(
              `Votre adresse a changé.\n\nAncienne : ${oldAddress}\nNouvelle : ${newAddress}\n\nVoulez-vous enregistrer cette nouvelle adresse dans votre profil ?`
            );
            
            if (shouldUpdate) {
              // Mettre à jour le profil avec la nouvelle adresse
              upsertProfile({
                name: formData.passengerName,
                address: formData.address,
                city: formData.city,
                postalCode: formData.postalCode,
                icon: formData.icon || undefined,
              });
            }
          } else {
            // Si l'adresse n'a pas changé, mettre à jour le profil normalement
            upsertProfile({
              name: formData.passengerName,
              address: formData.address,
              city: formData.city,
              postalCode: formData.postalCode,
              icon: formData.icon || undefined,
            });
          }
        } else {
          // Pas de profil existant, créer un nouveau profil
          upsertProfile({
            name: formData.passengerName,
            address: formData.address,
            city: formData.city,
            postalCode: formData.postalCode,
            icon: formData.icon || undefined,
          });
        }
      } catch {}

      // Sauvegarder le nom de l'utilisateur actuel dans localStorage pour l'annulation
      try {
        localStorage.setItem("cvtrg_current_user", formData.passengerName);
      } catch {}

      setSubmitted(true);
      setSubmittedCount(created);
      showNotification &&
        showNotification(
          created > 1
            ? `${created} demandes ont été créées.`
            : "Votre demande a été envoyée avec succès !",
          "success",
        );
      onSubmit && onSubmit({});
    } catch (err) {
      if (err.message === "DUPLICATE_REQUEST") {
        showNotification &&
          showNotification(
            "Une demande identique existe déjà pour cette date et cette adresse.",
            "warning",
          );
      } else {
        showNotification &&
          showNotification("Une erreur est survenue lors de l'envoi", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="passenger-form">
        <div className="form-header">
          <button className="back-button" onClick={onBack}>
            ← Retour
          </button>
          <h2>
            ✅ Demande{submittedCount > 1 ? "s" : ""} envoyée
            {submittedCount > 1 ? "s" : ""}
          </h2>
        </div>
        <div className="form">
          {formData.routine ? (
            <>
              <p>{submittedCount} prochaines réunions ont été enregistrées.</p>
              <p>
                Vous serez notifié ici lorsqu'un conducteur vous aura confirmé.
              </p>

              {/* Bouton pour voir la liste d'attente */}
              {onGoToWaitingList && (
                <div style={{ marginTop: "24px", textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={onGoToWaitingList}
                    className="take-btn"
                    style={{
                      fontSize: "16px",
                      padding: "12px 24px",
                      background: "#7c3aed",
                      border: "none",
                      borderRadius: "12px",
                      color: "white",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)",
                    }}
                  >
                    📋 Voir ma demande dans la liste d'attente
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <p>Votre demande pour :</p>
              <p>
                <strong>{formData.meetingLabel}</strong>
              </p>
              <p>
                est enregistrée. Vous serez notifié ici lorsqu'un conducteur
                vous aura confirmé.
              </p>

              {/* Bouton pour voir la liste d'attente */}
              {onGoToWaitingList && (
                <div style={{ marginTop: "24px", textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={onGoToWaitingList}
                    className="take-btn"
                    style={{
                      fontSize: "16px",
                      padding: "12px 24px",
                      background: "#7c3aed",
                      border: "none",
                      borderRadius: "12px",
                      color: "white",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)",
                    }}
                  >
                    📋 Voir ma demande dans la liste d'attente
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="passenger-form">
      <div className="form-header">
        <button className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <h2>🚶 Demande de transport</h2>
      </div>

      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label htmlFor="passengerName">Votre prénom :</label>
          <div style={{ position:'relative' }}>
            <NameAutocomplete
              value={formData.passengerName}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, passengerName: val }))
              }
              onSelectProfile={handleSelectProfile}
              placeholder="Entrez votre prénom"
            />
            <button
              type="button"
              onClick={() => setShowIconPicker(v => !v)}
              aria-label="Choisir une icône de profil"
              style={{
                position:'absolute', right:8, top:8, width:34, height:34,
                borderRadius:8, border:'1px solid #E5E7EB', background:'#fff',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 1px 2px rgba(0,0,0,.06)', cursor:'pointer',
                fontSize:20
              }}
            >
              {formData.icon || '👤'}
            </button>
          </div>
        </div>

        {/* Sélecteur d'icône (easter egg): masqué par défaut */}
        {showIconPicker && (
          <div className="form-group">
            <label>Icône du profil (facultatif) :</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {['', '👶','🧒','👧','🧑','👨','👩','👨‍🦱','👩‍🦱','👨‍🦰','👩‍🦰','👴','👵','👤','👥','🧑‍🦳','👩‍🦳'].map((emo) => (
                <button
                  key={emo || 'none'}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, icon: emo || undefined }))}
                  aria-label={emo ? `Choisir ${emo}` : 'Aucune icône'}
                  style={{
                    width:36, height:36, borderRadius:8,
                    border: formData.icon === emo ? '2px solid #7c3aed' : '1px solid #d1d5db',
                    background:'#fff', cursor:'pointer', fontSize:20
                  }}
                >
                  {emo || '—'}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Nombre de places demandées :</label>
          <div className="seats-selector">
            {[1, 2, 3, 4, 5].map((seats) => (
              <button
                key={seats}
                type="button"
                className={`seat-option ${formData.seatsNeeded === seats ? 'selected' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, seatsNeeded: seats }))}
                style={{
                  padding: '8px 16px',
                  margin: '4px',
                  border: formData.seatsNeeded === seats ? '2px solid #7c3aed' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: formData.seatsNeeded === seats ? '#7c3aed' : 'white',
                  color: formData.seatsNeeded === seats ? 'white' : '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                {seats} place{seats > 1 ? 's' : ''}
              </button>
            ))}
          </div>
          <div className="seats-info" style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>
            💡 <strong>Exemple :</strong> Famille Lambda = 3 places, 
            peut être prise en charge par plusieurs conducteurs
          </div>
        </div>

        <div className="form-group">
          <label>Votre adresse :</label>
          <AddressAutocomplete
            value={{
              address: formData.address,
              postcode: formData.postalCode,
              city: formData.city,
            }}
            onChange={handleAddressChange}
            placeholder="Ex: 12 rue de la Paix"
            showValidation={true}
          />

          {/* Bouton de géolocalisation intégré */}
          <button
            type="button"
            onClick={async () => {
              if (!navigator.geolocation) {
                alert(
                  "La géolocalisation n'est pas supportée par votre navigateur",
                );
                return;
              }

              try {
                const position = await new Promise((resolve, reject) => {
                  navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60000,
                  });
                });

                const { latitude, longitude } = position.coords;

                console.log("📍 Géolocalisation: Coordonnées GPS récupérées:", {
                  latitude,
                  longitude,
                });

                try {
                  // Utiliser l'API pour convertir les coordonnées en adresse
                  const response = await fetch(
                    `https://api-adresse.data.gouv.fr/reverse/?lon=${longitude}&lat=${latitude}`,
                  );
                  const data = await response.json();

                  console.log("🌐 API Reverse Geocoding réponse:", data);

                  if (data.features && data.features.length > 0) {
                    const feature = data.features[0];
                    const properties = feature.properties;

                    const realAddress = {
                      address:
                        properties.name ||
                        `${properties.housenumber || ""} ${properties.street || ""}`.trim(),
                      postcode: properties.postcode || "",
                      city: properties.city || "",
                    };

                    console.log("🏠 Adresse réelle trouvée:", realAddress);

                    setFormData((prev) => {
                      const updatedFormData = {
                        ...prev,
                        address: realAddress.address,
                        city: realAddress.city,
                        postalCode: realAddress.postcode,
                      };
                      console.log(
                        "📝 Géolocalisation: formData mis à jour avec vraie adresse:",
                        updatedFormData,
                      );
                      return updatedFormData;
                    });

                    alert(
                      "Localisation réussie ! Votre vraie adresse a été trouvée et remplie.",
                    );
                  } else {
                    // Fallback si l'API ne trouve rien
                    const fallbackAddress = {
                      address: "Adresse non trouvée",
                      postcode: "41000",
                      city: "Blois",
                    };

                    console.log(
                      "⚠️ Fallback: Adresse factice utilisée:",
                      fallbackAddress,
                    );

                    setFormData((prev) => {
                      const updatedFormData = {
                        ...prev,
                        address: fallbackAddress.address,
                        city: fallbackAddress.city,
                        postalCode: fallbackAddress.postcode,
                      };
                      console.log(
                        "📝 Géolocalisation: formData mis à jour avec fallback:",
                        updatedFormData,
                      );
                      return updatedFormData;
                    });

                    alert(
                      "Localisation réussie ! Coordonnées GPS récupérées (adresse approximative).",
                    );
                  }
                } catch (apiError) {
                  console.error("❌ Erreur API reverse geocoding:", apiError);

                  // Fallback en cas d'erreur API
                  const fallbackAddress = {
                    address: `Position GPS (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
                    postcode: "41000",
                    city: "Blois",
                  };

                  console.log("⚠️ Fallback après erreur API:", fallbackAddress);

                  setFormData((prev) => {
                    const updatedFormData = {
                      ...prev,
                      address: fallbackAddress.address,
                      city: fallbackAddress.city,
                      postalCode: fallbackAddress.postcode,
                    };
                    console.log(
                      "📝 Géolocalisation: formData mis à jour avec fallback après erreur:",
                      updatedFormData,
                    );
                    return updatedFormData;
                  });

                  alert(
                    "Localisation réussie ! Coordonnées GPS récupérées (adresse approximative).",
                  );
                }
              } catch (err) {
                if (err.code === 1) {
                  alert(
                    "Permission refusée. Veuillez autoriser la géolocalisation.",
                  );
                } else if (err.code === 2) {
                  alert("Position indisponible. Vérifiez votre connexion GPS.");
                } else if (err.code === 3) {
                  alert("Délai dépassé. Veuillez réessayer.");
                } else {
                  alert(`Erreur: ${err.message}`);
                }
              }
            }}
            disabled={submitting}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 20px",
              backgroundColor: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: submitting ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
              marginTop: "8px",
            }}
          >
            📍 Se localiser
          </button>
        </div>

        <div className="form-group">
          <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              name="returnDifferent"
              checked={!!formData.returnDifferent}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData((prev) => ({
                  ...prev,
                  returnDifferent: checked,
                  ...(checked
                    ? {}
                    : {
                        returnAddress: "",
                        returnCity: "",
                        returnPostalCode: "",
                        returnRealAddress: undefined,
                        returnLatitude: undefined,
                        returnLongitude: undefined,
                      }),
                }));
              }}
              disabled={submitting}
            />
            Adresse de retour différente ?
          </label>
        </div>

        {formData.returnDifferent && (
          <div className="form-group">
            <label>Adresse de retour :</label>
            <AddressAutocomplete
              value={{
                address: formData.returnAddress,
                postcode: formData.returnPostalCode,
                city: formData.returnCity,
              }}
              onChange={handleReturnAddressChange}
              placeholder="Ex: 8 avenue Victor Hugo"
              showValidation={true}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="description">Description (facultatif) :</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Ex: Je suis en fauteuil roulant, j'ai besoin d'aide pour monter dans la voiture..."
            rows={3}
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />
          <small style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
            Cette description sera visible dans le chat une fois que vous serez pris en charge.
          </small>
        </div>

        <div className="form-group">
          <label>Routine (création automatique des prochaines dates) :</label>
          <div
            className={`routine-option ${formData.routine ? "selected" : ""}`}
            onClick={() =>
              setFormData((prev) => ({ ...prev, routine: !prev.routine }))
            }
          >
            <span>
              🚀 Activer la routine - Créer automatiquement les demandes pour
              les 8 prochaines réunions
            </span>
          </div>
        </div>

        {!formData.routine && (
          <div className="form-group">
            <label>Date(s) de réunion :</label>
            <MeetingDatePicker
              values={formData.selectedMeetings}
              value={formData.meetingAt}
              onChange={handleMeetingChange}
              multi={true}
            />
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting
              ? "Envoi…"
              : formData.routine
                ? "Créer les prochaines demandes"
                : "Envoyer la demande"}
          </button>
        </div>
      </form>

      <div className="form-info">
        <p>📝 Vos demandes seront visibles par les conducteurs disponibles.</p>
      </div>
    </div>
  );
};

export default PassengerForm;
